import "./ManageUser.scss";

import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import AbunTable from "components/AbunTable/AbunTable";
import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import "./integration.scss";

export interface ArticleTitle {
	articleUID: string
	articleTitle: string
	createdOn: string
	keyword: string
	keywordTraffic: number | null
	internalLinks: number | null
	externalLinks: number | null
	images: number | null
	wordCount: number | null
	isProcessing: boolean
	isGenerated: boolean
	isPosted: boolean
	isFailed: boolean
	isArchived: boolean
	postLink: string
	isUserAdded: boolean
	postedTo: string
	feedback: string
	postedOn: string
}

export default function Articles(props: { articles: Array<ArticleTitle>, showDateColumn?: boolean, userID?: string }) {
	// ---------------------- NON STATE CONSTANTS ----------------------
	const pageSizes = [10, 50, 150, 250, 500];
	const [showArticles, setArticles] = useState(false);

	// ---------------------- TABLE COLUMN DEFS ----------------------
	const columnHelper = createColumnHelper<ArticleTitle>();
	const columnDefs: ColumnDef<any, any>[] = [
		columnHelper.accessor((row: ArticleTitle) => row.articleTitle, {
			id: 'title',
			header: "Article Title",
			cell: props => {
				if (props.row.original.isProcessing || props.row.original.isGenerated) {
					return (
						<NavLink to={`/view-article/${props.row.original.articleUID}`} >
							{props.row.original.articleTitle}
						</NavLink>
					)
				} else {
					return props.row.original.articleTitle;
				}
			},
			enableGlobalFilter: true,
			enableSorting: true
		}),

		columnHelper.accessor((row: ArticleTitle) => row.createdOn, {
			id: 'created_on',
			header: "Created On",
			cell: info => info.getValue(),
			enableGlobalFilter: false,
		}),

		columnHelper.accessor((row: ArticleTitle) => row.articleUID, {
			id: 'article_uid',
			header: "Article UID",
			cell: info => info.getValue(),
			enableGlobalFilter: true,
			enableSorting: true
		}),
		columnHelper.accessor((row: ArticleTitle) => row.keyword, {
			id: 'keyword',
			header: "Keyword [Traffic]",
			cell: props => {
				return (
					`${props.row.original.keyword} [${props.row.original.keywordTraffic}]`
				)
			},
			enableGlobalFilter: true,
			enableSorting: true
		}),
		columnHelper.accessor((row: ArticleTitle) => row.wordCount, {
			id: 'word_cound',
			header: "Word Count",
			cell: info => {
				if (info.getValue()) {
					return info.getValue()
				} else {
					return "---"
				}
			},
			enableGlobalFilter: false,
			enableSorting: true
		}),
		columnHelper.display({
			id: 'is_generated',
			header: "Generated",
			cell: info => {
				if (info.getValue()) {
					return "Yes"
				} else {
					return "No"
				}
			},
			enableGlobalFilter: false,
		}),
		columnHelper.display({
			id: 'is_processing',
			header: "Processing",
			cell: info => {
				if (info.getValue()) {
					return "Yes"
				} else {
					return "No"
				}
			},
			enableGlobalFilter: false,
		}),
		columnHelper.display({
			id: 'is_archived',
			header: "Archived",
			cell: info => {
				if (info.getValue()) {
					return "Yes"
				} else {
					return "No"
				}
			},
			enableGlobalFilter: false,
		}),
		columnHelper.display({
			id: 'posted_to',
			header: "Posted To",
			cell: props => {
				if (props.row.original.isPosted) {
					return props.row.original.postedTo
				} else {
					return "--"
				}
			},
			enableGlobalFilter: true,
		}),
		columnHelper.accessor((row: ArticleTitle) => row.feedback, {
			id: 'feedback',
			header: "Feedback",
			cell: info => {
				// only show feedback if article is generated and feedback is not "no_feedback"
				if (info.row.original.isGenerated && info.getValue() !== "no_feedback") {
					// capitalize first letter
					return info.getValue().charAt(0).toUpperCase() + info.getValue().slice(1);
				} else {
					return "---"
				}
			},
			enableGlobalFilter: false,
		}),
		columnHelper.display({
			id: 'article_link',
			header: "Article Link",
			cell: props => {
				if (props.row.original.isPosted) {
					return (
						<Link target="_blank" to={props.row.original.postLink} className={"button is-primary"}>
							View
						</Link>
					)
				} else {
					return (
						<button style={{ backgroundColor: "#86a5ec", cursor: "not-allowed" }} className="button is-primary">
							View
						</button>
					)
				}
			},
			enableGlobalFilter: false,
		}),
	]

	if (props.showDateColumn) {
		const dateColumn = columnHelper.accessor((row: ArticleTitle) => row.postedOn, {
			id: 'date',
			header: "Published Date",
			cell: info => {
				const value = info.getValue();
				if (!value) {
					return "---";
				}
				const date = new Date(value);
				return date.toLocaleString();
			},
			enableGlobalFilter: false,
		});

		columnDefs.splice(columnDefs.length - 1, 0, dateColumn);
	}

	// ==============================================================
	// ---------------------- MAIN RENDER CODE ----------------------
	// ==============================================================
	return (
		<div className={"card w-100 mt-4"}>
			<header
				className="card-header is-clickable"
				onClick={() => setArticles(!showArticles)}
				>
				<p className="card-header-title">
				    Articles
				</p>
				<span className="card-header-icon">
				  <FontAwesomeIcon icon={showArticles ? faChevronDown : faChevronRight} />
				</span>
			</header>
			{showArticles && (
			<div className={"card-content"}>
				<div className={"content"}>
					<div className={"all-users-container"}>
						<AbunTable tableContentName={"Articles"}
							tableData={props.articles}
							columnDefs={columnDefs}
							pageSizes={pageSizes}
							initialPageSize={pageSizes[0]}
							noDataText={"No Articel Found"}
							enableSorting={true}
							searchboxPlaceholderText={"Search articles..."} />
					</div>
				</div>
			</div>
			)}
		</div>
	)
}
